import React, { useState, useEffect, useContext, SyntheticEvent } from 'react';
import { DateTime, type DateTime as DateTimeType } from 'luxon';

import { PageContext } from '../components/page';
import { FlightStatuses } from '../../types/flight';
import { DropdownOption } from '../../components/Dropdown';
import { toTitleCase } from '../../lib/strings';
import { TextInput, DateInput, DropdownInput } from './FilterInputs';
import FormButtonRow from '../components/formButtonRow';
import DateSelector from '../../lib/DateSelector/DateSelector';
import { FilterabeledInputWrapper } from '../../components/forms/Input';
import { DATE_PICKER_DEFAULT_FORMAT, FLIGHT_SEARCH_DATE_FORMAT } from '../../constants';

interface FlightSearchFilterProps {
  airlineIataFilter: string;
  setAirlineIataFilter: (value: string) => void;
  flightNumberFilter: string;
  setFlightNumberFilter: (value: string) => void;
  departureDateFilter: string | null;
  setDepartureDateFilter: (value: string | null) => void;
  arrivalDateFilter: string | null;
  setArrivalDateFilter: (value: string | null) => void;
  departureAirportIataFilter: string;
  setDepartureAirportIataFilter: (value: string) => void;
  arrivalAirportIataFilter: string;
  setArrivalAirportIataFilter: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  loading: boolean;
  onSubmit: (isSubmitting?: boolean) => void;
  onClear: () => void;
}

const validateDate = (value: string): string => {
  if (!value) return '';
  const dateRegex = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
  // extra date format test since it just assumes certain pieces if they are missing
  if (!dateRegex.test(value)) {
    return 'Invalid date';
  }

  const dateValue = DateTime.fromISO(value);
  const maxDate = DateTime.now().plus({ days: 3 });
  // If invalid date format throw error
  if (!(dateValue instanceof DateTime) || !dateValue.isValid) {
    return 'Invalid date';
  }
  // date must be within the last 2 years through the upcoming 3 days
  else if (dateValue > maxDate) {
    return `Must be on or before ${maxDate.toFormat('MM/dd/yyyy')}`;
  } else if (dateValue <= DateTime.now().minus({ year: 2 })) {
    return 'Must be within the past 2 years';
  }
  return '';
};

const validateAirport = (value: string): string => {
  return /^([A-Za-z]{3})?$/.test(value)
    ? ''
    : ' ';
};

export const FlightSearchFilter: React.FC<FlightSearchFilterProps> = ({
  airlineIataFilter,
  setAirlineIataFilter,
  flightNumberFilter,
  setFlightNumberFilter,
  departureDateFilter,
  setDepartureDateFilter,
  arrivalDateFilter,
  setArrivalDateFilter,
  departureAirportIataFilter,
  setDepartureAirportIataFilter,
  arrivalAirportIataFilter,
  setArrivalAirportIataFilter,
  statusFilter,
  setStatusFilter,
  loading,
  onSubmit,
  onClear,
}) => {
  const { query } = useContext(PageContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [airlineError, setAirlineError] = useState('');
  const [flightNumberError, setFlightNumberError] = useState('');
  const [departureDateError, setDepartureDateError] = useState('');
  const [arrivalDateError, setArrivalDateError] = useState('');
  const [departureAirportError, setDepartureAirportIataError] = useState('');
  const [arrivalAirportError, setArrivalAirportIataError] = useState('');
  const [statusError, setStatusError] = useState('');

  const airlineIataKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      setAirlineIataFilter(target?.value?.trim());
    }
  };

  const flightNumberKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      const value = target.value.trim();
      if (/^\d*$/.test(value)) { // Allow only numeric input
        setFlightNumberFilter(value);
      }
    }
  };

  const departureDateKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      setDepartureDateFilter(target?.value);
    }
  };

  const arrivalDateKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      setArrivalDateFilter(target?.value);
    }
  };

  const departureAirportIataKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      setDepartureAirportIataFilter(target?.value?.trim());
    }
  };

  const arrivalAirportIataKeypressHandler = (
    event: SyntheticEvent<HTMLInputElement>,
  ) => {
    const target = event?.target;
    if (target instanceof window.HTMLInputElement) {
      setArrivalAirportIataFilter(target?.value?.trim());
    }
  };

  const statusOptions: DropdownOption<string>[] = [
    {
      value: '',
      text: 'Any',
    },
    ...Object.keys(FlightStatuses).map(status => {
      return {
        value: status,
        text: toTitleCase(status),
      };
    }),
  ];
  // Store values in const to avoid reevaluation on every validation
  const statusValues = statusOptions.map(a => a.value);

  const statusChangeHandler = (value: string | number | readonly string[] | undefined) => {
    setStatusFilter(value ? value.toString() : '');
  };

  // run validation and determine whether search button is disabled
  const validateFormValues = () => {
    let isError = false;
    // airline
    let message = [0, 2, 3].includes(airlineIataFilter?.length)
      ? ''
      : ' ';
    setAirlineError(message);
    message && (isError = true);

    // flight number
    message =  [0, 1, 2, 3, 4].includes(flightNumberFilter?.length)
      ? ''
      : ' ';
    setFlightNumberError(message);
    message && (isError = true);

    // departure date
    message = validateDate(departureDateFilter?.toString() || '');
    setDepartureDateError(message);
    message && (isError = true);

    // arrival date
    message = validateDate(arrivalDateFilter?.toString() || '');
    setArrivalDateError(message);
    message && (isError = true);

    // 1 date much be filled out to submit
    if (!departureDateFilter && !arrivalDateFilter) {
      setDepartureDateError('Departure or Arrival date must be set');
      isError = true;
    }

    // departure airport
    message = validateAirport(departureAirportIataFilter);
    setDepartureAirportIataError(message);
    message && (isError = true);

    // arrival airport
    message = validateAirport(arrivalAirportIataFilter);
    setArrivalAirportIataError(message);
    message && (isError = true);

    // status
    message = statusValues.includes(statusFilter?.replace('ANY', ''))
      ? ''
      : `${statusFilter} is an invalid status`;
    setStatusError(message);
    message && (isError = true);

    return (
      isError ||
      !(
        airlineIataFilter ||
        flightNumberFilter ||
        departureDateFilter ||
        arrivalDateFilter ||
        departureAirportIataFilter ||
        arrivalAirportIataFilter ||
        statusFilter
      )
    );
  };

  const clearHandler = () => {
    setAirlineIataFilter('');
    setAirlineError('');
    setFlightNumberFilter('');
    setFlightNumberError('');
    setDepartureDateFilter(DateTime.now().toFormat('yyyy-MM-dd'));
    setDepartureDateError('');
    setArrivalDateFilter(null);
    setArrivalDateError('');
    setDepartureAirportIataFilter('');
    setDepartureAirportIataError('');
    setArrivalAirportIataFilter('');
    setArrivalAirportIataError('');
    setStatusFilter('');
    setStatusError('');
    setIsDisabled(false);
    onClear();
  };

  useEffect(() => {
    setIsDisabled(validateFormValues());
  }, [
    airlineIataFilter,
    flightNumberFilter,
    departureDateFilter,
    arrivalDateFilter,
    departureAirportIataFilter,
    arrivalAirportIataFilter,
    statusFilter,
  ]);

  // Execute the query again if it's not currently loading, there are
  // search params present, and the form validates correctly
  useEffect(() => {
    if (!loading && location.search && !validateFormValues()) {
      onSubmit(false);
    }
  }, [query]);

  return (
    <>
      <TextInput
        id="airlineIata"
        label="Airline"
        filter={airlineIataFilter}
        keypressHandler={airlineIataKeypressHandler}
        error={airlineError}
        maxLength={3}
      />

      <TextInput
        id="flightNumber"
        label="Flight Number"
        filter={flightNumberFilter}
        keypressHandler={flightNumberKeypressHandler}
        error={flightNumberError}
      />
      <div className='flight-search-datepicker'>
        <FilterabeledInputWrapper width={'max-content'}>
          <DateSelector
            key={`departureDate`}
            label="Departure Date"
            dateFormat={DATE_PICKER_DEFAULT_FORMAT}
            value={departureDateFilter ? DateTime.fromFormat(departureDateFilter, FLIGHT_SEARCH_DATE_FORMAT).toFormat(DATE_PICKER_DEFAULT_FORMAT) : null} // Convert string to Date
            onChange={(value: Date | null) =>
              setDepartureDateFilter(value ? DateTime.fromJSDate(value).toFormat(FLIGHT_SEARCH_DATE_FORMAT) : null)
            }
            maxDate={DateTime.now().plus({ days: 3 }).toFormat(FLIGHT_SEARCH_DATE_FORMAT)}
          />
        </FilterabeledInputWrapper>
      </div>
      <div className='flight-search-datepicker'>
        <FilterabeledInputWrapper width={'max-content'}>
          <DateSelector
            key={`arrivalDate`}
            label="Arrival Date"
            dateFormat={DATE_PICKER_DEFAULT_FORMAT}
            value={arrivalDateFilter ? DateTime.fromFormat(arrivalDateFilter, FLIGHT_SEARCH_DATE_FORMAT).toFormat(DATE_PICKER_DEFAULT_FORMAT) : null} // Convert string to Date
            onChange={(value: Date | null) =>
              setArrivalDateFilter(value ? DateTime.fromJSDate(value).toFormat(FLIGHT_SEARCH_DATE_FORMAT) : null)
            }
            maxDate={DateTime.now().plus({ days: 3 }).toFormat(FLIGHT_SEARCH_DATE_FORMAT)}
          />
        </FilterabeledInputWrapper>
      </div>
      <TextInput
        id="arrivalAirportIata"
        label="Departure Airport"
        filter={departureAirportIataFilter}
        keypressHandler={departureAirportIataKeypressHandler}
        error={departureAirportError}
        maxLength={3}
      />

      <TextInput
        id="departureAirportIata"
        label="Arrival Airport"
        filter={arrivalAirportIataFilter}
        keypressHandler={arrivalAirportIataKeypressHandler}
        error={arrivalAirportError}
        maxLength={3}
      />

      <DropdownInput
        id="status"
        label="Status"
        filter={statusFilter}
        dropdownOptions={statusOptions}
        keypressHandler={statusChangeHandler}
        error={statusError}
      />
      <FormButtonRow
        onClear={clearHandler}
        onSubmit={onSubmit}
        saveDisabled={isDisabled}
      />
    </>
  );
};
